import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  MessageSquare,
  ListTodo,
  Mail,
  Users,
  FileText,
  Clock,
  Bell,
  Send,
  Plus,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Paperclip,
  Image as ImageIcon,
  Megaphone,
  ChevronRight,
  Filter,
  Search,
  RefreshCw,
  User,
  Building2,
  Calendar,
  CalendarDays,
  Star,
  X,
  Download,
  File,
  Gavel,
  UserPlus,
  Trash2,
  Edit2,
  MapPin,
  ArrowRightLeft,
  Briefcase,
  Vote,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TASK_TYPES = [
  { id: 'send_notice', name: 'Send Notice to Residents' },
  { id: 'obtain_quotes', name: 'Obtain Quotes' },
  { id: 'schedule_maintenance', name: 'Schedule Maintenance' },
  { id: 'upload_document', name: 'Upload Document' },
  { id: 'financial_report', name: 'Financial Report' },
  { id: 'general', name: 'General Request' },
  { id: 'other', name: 'Other' },
];

const ANNOUNCEMENT_CATEGORIES = [
  { id: 'general', name: 'General Notice' },
  { id: 'urgent', name: 'Urgent Notice' },
  { id: 'maintenance', name: 'Maintenance' },
  { id: 'event', name: 'Event' },
  { id: 'financial', name: 'Financial' },
];

export default function TrusteeHub() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('messages');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Data states
  const [chatRooms, setChatRooms] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [emails, setEmails] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  // UI states
  const [activeChatRoom, setActiveChatRoom] = useState(null);
  const [activeChatType, setActiveChatType] = useState('agent_chat');
  const [messageInput, setMessageInput] = useState('');
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [taskFilter, setTaskFilter] = useState('all');
  
  // Form states
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    task_type: 'general',
    priority: 'medium',
    due_date: '',
  });
  
  const [emailForm, setEmailForm] = useState({
    subject: '',
    body: '',
  });
  
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    category: 'general',
  });

  // Transfer to Managing Agent states
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferForm, setTransferForm] = useState({
    target_company_email: '',
    target_company_name: '',
    notes: ''
  });
  const [searchingCompany, setSearchingCompany] = useState(false);
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [myTransferRequests, setMyTransferRequests] = useState([]);

  // Invite Managing Agent states
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    company_name: '',
    agent_email: '',
    message: ''
  });
  const [myInvitations, setMyInvitations] = useState([]);
  const [sendingInvite, setSendingInvite] = useState(false);

  // File attachment states
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('session_token');
  const hoaId = user?.hoa_id || 'demo_hoa';

  // Check access
  const hasAccess = user?.role === 'trustee' || 
                    user?.role === 'managing_agent' || 
                    user?.role === 'agent_admin' ||
                    user?.role === 'property_manager' ||
                    user?.role === 'platform_owner' ||
                    user?.is_trustee === true;

  useEffect(() => {
    if (hasAccess) {
      fetchData();
    }
  }, [hasAccess]);

  useEffect(() => {
    if (activeChatRoom) {
      fetchMessages(activeChatRoom);
    }
  }, [activeChatRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/trustee-hub/stats`, { headers });
      if (statsRes.ok) setStats(await statsRes.json());
      
      // Fetch chat rooms
      const roomsRes = await fetch(`${API_URL}/api/trustee-hub/messages/rooms/list`, { headers });
      if (roomsRes.ok) setChatRooms(await roomsRes.json());
      
      // Fetch tasks
      const tasksRes = await fetch(`${API_URL}/api/trustee-hub/tasks/list`, { headers });
      if (tasksRes.ok) setTasks(await tasksRes.json());
      
      // Fetch emails
      const emailsRes = await fetch(`${API_URL}/api/trustee-hub/emails/list`, { headers });
      if (emailsRes.ok) setEmails(await emailsRes.json());
      
      // Fetch announcements
      const annRes = await fetch(`${API_URL}/api/trustee-hub/announcements/list`, { headers });
      if (annRes.ok) setAnnouncements(await annRes.json());
      
      // Fetch templates
      const tmplRes = await fetch(`${API_URL}/api/trustee-hub/templates/list`, { headers });
      if (tmplRes.ok) setTemplates(await tmplRes.json());
      
      // Fetch logs
      const logsRes = await fetch(`${API_URL}/api/trustee-hub/logs/list?limit=50`, { headers });
      if (logsRes.ok) setLogs(await logsRes.json());
      
      // Fetch notifications
      const notifRes = await fetch(`${API_URL}/api/trustee-hub/notifications/list?unread_only=true`, { headers });
      if (notifRes.ok) setNotifications(await notifRes.json());
      
      // Fetch my transfer requests
      const transferRes = await fetch(`${API_URL}/api/building-transfers/my-requests`, { 
        credentials: 'include',
        headers 
      });
      if (transferRes.ok) setMyTransferRequests(await transferRes.json());
      
      // Fetch my agent invitations
      const inviteRes = await fetch(`${API_URL}/api/agent-invitations/sent`, { headers });
      if (inviteRes.ok) setMyInvitations(await inviteRes.json());
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (chatRoomId) => {
    try {
      const res = await fetch(`${API_URL}/api/trustee-hub/messages/${chatRoomId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMessages(await res.json());
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (attachmentData = null) => {
    if (!messageInput.trim() && !attachmentData) return;
    
    try {
      const messageData = {
        chat_type: activeChatType,
        chat_room_id: activeChatRoom,
        content: messageInput.trim() || (attachmentData ? `Shared a ${attachmentData.type}` : ''),
        message_type: attachmentData ? attachmentData.type : 'text',
        attachment_url: attachmentData?.url || null,
        attachment_name: attachmentData?.name || null
      };

      const res = await fetch(`${API_URL}/api/trustee-hub/messages/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messageData)
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessageInput('');
        clearFileSelection();
        if (!activeChatRoom) {
          setActiveChatRoom(data.chat_room_id);
        }
        fetchMessages(data.chat_room_id);
        fetchData(); // Refresh rooms list
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const clearFileSelection = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadAndSend = async () => {
    if (!selectedFile) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const uploadRes = await fetch(`${API_URL}/api/trustee-hub/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (uploadRes.ok) {
        const uploadData = await uploadRes.json();
        const isImage = selectedFile.type.startsWith('image/');
        
        await handleSendMessage({
          type: isImage ? 'image' : 'document',
          url: uploadData.url,
          name: uploadData.filename
        });
        
        toast.success('File sent successfully');
      } else {
        toast.error('Failed to upload file');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return ImageIcon;
    if (['pdf'].includes(ext)) return FileText;
    return File;
  };

  const isImageFile = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  };

  const handleCreateTask = async () => {
    if (!taskForm.title || !taskForm.description) {
      toast.error('Title and description are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/trustee-hub/tasks/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(taskForm)
      });
      
      if (res.ok) {
        toast.success('Task created successfully');
        setShowTaskDialog(false);
        setTaskForm({ title: '', description: '', task_type: 'general', priority: 'medium', due_date: '' });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/trustee-hub/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success(`Task marked as ${newStatus}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.body) {
      toast.error('Subject and message are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/trustee-hub/emails/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailForm)
      });
      
      if (res.ok) {
        toast.success('Email sent to managing agent');
        setShowEmailDialog(false);
        setEmailForm({ subject: '', body: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title || !announcementForm.content) {
      toast.error('Title and content are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/trustee-hub/announcements/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(announcementForm)
      });
      
      if (res.ok) {
        toast.success('Announcement submitted for approval');
        setShowAnnouncementDialog(false);
        setAnnouncementForm({ title: '', content: '', category: 'general' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create announcement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveAnnouncement = async (announcementId) => {
    try {
      const res = await fetch(`${API_URL}/api/trustee-hub/announcements/${announcementId}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Announcement approved and sent to residents');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to approve announcement');
    }
  };

  const handleUseTemplate = (template) => {
    setSelectedTemplate(template);
    
    if (template.template_type === 'task') {
      setTaskForm({
        ...taskForm,
        title: template.subject || template.name,
        description: template.body,
        task_type: template.category === 'notice' ? 'send_notice' : 
                   template.category === 'request' ? 'obtain_quotes' : 'general'
      });
      setShowTemplateDialog(false);
      setShowTaskDialog(true);
    } else if (template.template_type === 'email') {
      setEmailForm({
        subject: template.subject || template.name,
        body: template.body
      });
      setShowTemplateDialog(false);
      setShowEmailDialog(true);
    } else if (template.template_type === 'announcement') {
      setAnnouncementForm({
        title: template.subject || template.name,
        content: template.body,
        category: template.category === 'notice' ? 'general' : template.category
      });
      setShowTemplateDialog(false);
      setShowAnnouncementDialog(true);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' });
  };

  const getTaskTypeLabel = (type) => TASK_TYPES.find(t => t.id === type)?.name || type;
  
  const getPriorityBadge = (priority) => {
    const colors = {
      low: 'bg-slate-100 text-slate-600',
      medium: 'bg-amber-100 text-amber-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-slate-100 text-slate-600',
      pending_approval: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      sent: 'bg-emerald-100 text-emerald-700'
    };
    return colors[status] || colors.pending;
  };

  const filteredTasks = tasks.filter(t => {
    if (taskFilter === 'all') return true;
    return t.status === taskFilter;
  });

  const isAgent = ['managing_agent', 'agent_admin', 'property_manager', 'platform_owner'].includes(user?.role);

  // Transfer handlers
  const handleSearchCompanies = async (query) => {
    if (query.length < 2) {
      setCompanySearchResults([]);
      return;
    }
    
    setSearchingCompany(true);
    try {
      const response = await fetch(`${API_URL}/api/building-transfers/companies/search?q=${encodeURIComponent(query)}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setCompanySearchResults(await response.json());
      }
    } catch (error) {
      console.error('Error searching companies:', error);
    } finally {
      setSearchingCompany(false);
    }
  };

  const handleSelectCompany = (company) => {
    setSelectedCompany(company);
    setTransferForm({
      ...transferForm,
      target_company_email: company.email || '',
      target_company_name: company.name
    });
    setCompanySearchResults([]);
  };

  const handleSubmitTransfer = async () => {
    if (!transferForm.target_company_email && !transferForm.target_company_name) {
      toast.error('Please select or enter a managing agent company');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/building-transfers/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          hoa_id: hoaId,
          target_company_email: transferForm.target_company_email,
          target_company_name: transferForm.target_company_name,
          notes: transferForm.notes
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Transfer request sent to ${data.target_company}`);
        setShowTransferDialog(false);
        setTransferForm({ target_company_email: '', target_company_name: '', notes: '' });
        setSelectedCompany(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send transfer request');
      }
    } catch (error) {
      toast.error('Failed to send transfer request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelTransfer = async (transferId) => {
    try {
      const response = await fetch(`${API_URL}/api/building-transfers/cancel/${transferId}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Transfer request cancelled');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel transfer');
      }
    } catch (error) {
      toast.error('Failed to cancel transfer');
    }
  };

  // =============== AGENT INVITATION FUNCTIONS ===============
  
  const handleSendInvitation = async () => {
    if (!inviteForm.agent_email || !inviteForm.company_name) {
      toast.error('Please enter the agent email and company name');
      return;
    }
    
    setSendingInvite(true);
    try {
      const response = await fetch(`${API_URL}/api/agent-invitations/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          hoa_id: hoaId,
          company_name: inviteForm.company_name,
          agent_email: inviteForm.agent_email,
          message: inviteForm.message
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Invitation sent to ${inviteForm.agent_email}! They will receive an email notification.`);
        setShowInviteDialog(false);
        setInviteForm({ company_name: '', agent_email: '', message: '' });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send invitation');
      }
    } catch (error) {
      console.error('Invitation error:', error);
      toast.error('Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      const response = await fetch(`${API_URL}/api/agent-invitations/cancel/${invitationId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Invitation cancelled');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel invitation');
      }
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-slate-500 mb-4">
              The Trustee Hub is only available to verified trustees and managing agents.
            </p>
            <Button onClick={() => navigate('/admin')}>
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="trustee-hub">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Trustee Hub</h1>
                <p className="text-sm text-slate-500">Communication & Task Management</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Invite Managing Company - Full directory + replace flow */}
              {!isAgent && (
                <Button
                  className="hidden sm:flex bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={() => navigate(`/admin/buildings/${hoaId}/invite-agent`)}
                  data-testid="invite-managing-company-btn"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Invite Managing Company
                </Button>
              )}
              {/* Invite Managing Agent - Only for trustees who don't have an agent yet */}
              {!isAgent && (
                <Button 
                  className="hidden sm:flex bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={() => setShowInviteDialog(true)}
                  data-testid="invite-agent-btn"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Invite Agent
                </Button>
              )}
              {/* Transfer to Managing Agent - Only for trustees, not agents */}
              {!isAgent && (
                <Button 
                  variant="outline"
                  className="hidden sm:flex bg-amber-50 border-amber-200 hover:bg-amber-100 text-amber-700"
                  onClick={() => setShowTransferDialog(true)}
                  data-testid="transfer-building-btn"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Transfer to Agent
                </Button>
              )}
              <Button 
                variant="outline" 
                className="hidden sm:flex"
                onClick={() => navigate('/governance')}
                data-testid="governance-link"
              >
                <Gavel className="w-4 h-4 mr-2" />
                Governance & Resolutions
              </Button>
              <Button variant="ghost" size="icon" className="relative" onClick={() => setActiveTab('notifications')}>
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {notifications.length}
                  </span>
                )}
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/admin/residents')}>
                <UserPlus className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Residents</span>
              </Button>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4">
        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('messages')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.unread_messages}</p>
                    <p className="text-xs text-slate-500">Unread Messages</p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-indigo-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('tasks')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.tasks.pending}</p>
                    <p className="text-xs text-slate-500">Pending Tasks</p>
                  </div>
                  <ListTodo className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setActiveTab('tasks'); setTaskFilter('in_progress'); }}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.tasks.in_progress}</p>
                    <p className="text-xs text-slate-500">In Progress</p>
                  </div>
                  <Clock className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('announcements')}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{stats.pending_announcements}</p>
                    <p className="text-xs text-slate-500">Pending Approval</p>
                  </div>
                  <Megaphone className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Voting Section - Prominent CTA */}
        <Card 
          className="mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-all border-0"
          onClick={() => navigate('/admin/voting')}
          data-testid="voting-section-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Vote className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Voting & Resolutions</h3>
                  <p className="text-white/80 text-sm">Create and manage building votes for AGM and trustee decisions</p>
                </div>
              </div>
              <ChevronRight className="w-6 h-6 text-white/60" />
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-4 lg:grid-cols-9 h-auto gap-1 bg-slate-100 p-1">
            <TabsTrigger value="messages" className="text-xs py-2">
              <MessageSquare className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Messages</span>
            </TabsTrigger>
            <TabsTrigger value="tasks" className="text-xs py-2">
              <ListTodo className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Tasks</span>
            </TabsTrigger>
            <TabsTrigger value="emails" className="text-xs py-2">
              <Mail className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Emails</span>
            </TabsTrigger>
            <TabsTrigger value="trustee-chat" className="text-xs py-2">
              <Users className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Trustee Chat</span>
            </TabsTrigger>
            <TabsTrigger value="announcements" className="text-xs py-2">
              <Megaphone className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Announce</span>
            </TabsTrigger>
            <TabsTrigger value="calendar" className="text-xs py-2">
              <CalendarDays className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Calendar</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="text-xs py-2">
              <File className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Documents</span>
            </TabsTrigger>
            <TabsTrigger value="templates" className="text-xs py-2">
              <FileText className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Templates</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="text-xs py-2">
              <Clock className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
            {!isAgent && (
              <TabsTrigger value="transfers" className="text-xs py-2">
                <ArrowRightLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Transfers</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* Messages Tab - Agent Chat */}
          <TabsContent value="messages" className="mt-4">
            <Card className="h-[60vh]">
              <CardHeader className="py-3 px-4 border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                    Chat with Managing Agent
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-0 flex flex-col h-[calc(60vh-60px)]">
                <ScrollArea className="flex-1 p-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No messages yet. Start a conversation!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {messages.map(msg => (
                        <div 
                          key={msg.message_id}
                          className={`flex ${msg.sender_id === user?.user_id ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                            msg.sender_id === user?.user_id 
                              ? 'bg-indigo-600 text-white rounded-br-md' 
                              : 'bg-slate-100 text-slate-900 rounded-bl-md'
                          }`}>
                            {msg.sender_id !== user?.user_id && (
                              <p className="text-xs font-medium mb-1 opacity-70">{msg.sender_name}</p>
                            )}
                            
                            {/* Attachment Preview */}
                            {msg.attachment_url && (
                              <div className="mb-2">
                                {msg.message_type === 'image' || isImageFile(msg.attachment_name) ? (
                                  <div className="rounded-lg overflow-hidden">
                                    <img 
                                      src={`${API_URL}${msg.attachment_url}`} 
                                      alt={msg.attachment_name}
                                      className="max-w-full max-h-48 rounded-lg cursor-pointer hover:opacity-90"
                                      onClick={() => window.open(`${API_URL}${msg.attachment_url}`, '_blank')}
                                    />
                                  </div>
                                ) : (
                                  <a 
                                    href={`${API_URL}${msg.attachment_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-2 p-2 rounded-lg ${
                                      msg.sender_id === user?.user_id 
                                        ? 'bg-indigo-500 hover:bg-indigo-400' 
                                        : 'bg-slate-200 hover:bg-slate-300'
                                    }`}
                                  >
                                    <File className="w-5 h-5" />
                                    <span className="text-sm truncate max-w-[150px]">{msg.attachment_name}</span>
                                    <Download className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                            )}
                            
                            {msg.content && <p className="text-sm">{msg.content}</p>}
                            <p className={`text-xs mt-1 ${msg.sender_id === user?.user_id ? 'text-indigo-200' : 'text-slate-400'}`}>
                              {formatDateShort(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </ScrollArea>
                
                {/* File Preview */}
                {selectedFile && (
                  <div className="px-3 py-2 border-t bg-slate-50">
                    <div className="flex items-center gap-3">
                      {filePreview ? (
                        <img src={filePreview} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                      ) : (
                        <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center">
                          <File className="w-8 h-8 text-slate-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{selectedFile.name}</p>
                        <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={clearFileSelection}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Message Input */}
                <div className="p-3 border-t flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                  >
                    <Paperclip className="w-5 h-5 text-slate-500" />
                  </Button>
                  <Input
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder={selectedFile ? "Add a caption (optional)..." : "Type a message..."}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        if (selectedFile) {
                          handleUploadAndSend();
                        } else {
                          handleSendMessage();
                        }
                      }
                    }}
                    className="flex-1"
                  />
                  {selectedFile ? (
                    <Button onClick={handleUploadAndSend} disabled={uploadingFile}>
                      {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  ) : (
                    <Button onClick={() => handleSendMessage()} disabled={!messageInput.trim()}>
                      <Send className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="mt-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div className="flex gap-2">
                {['all', 'pending', 'in_progress', 'completed'].map(filter => (
                  <Button
                    key={filter}
                    variant={taskFilter === filter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTaskFilter(filter)}
                  >
                    {filter === 'all' ? 'All' : filter.replace('_', ' ')}
                  </Button>
                ))}
              </div>
              <Button onClick={() => setShowTaskDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                New Task
              </Button>
            </div>
            
            {filteredTasks.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <ListTodo className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No Tasks</h3>
                  <p className="text-slate-500 mb-4">Create a task to request action from your managing agent.</p>
                  <Button onClick={() => setShowTaskDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Task
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredTasks.map(task => (
                  <Card key={task.task_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getPriorityBadge(task.priority)}>{task.priority}</Badge>
                            <Badge variant="outline">{getTaskTypeLabel(task.task_type)}</Badge>
                          </div>
                          <h4 className="font-semibold text-slate-900">{task.title}</h4>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{task.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {task.created_by_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDateShort(task.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <Badge className={getStatusBadge(task.status)}>{task.status.replace('_', ' ')}</Badge>
                          {isAgent && task.status === 'pending' && (
                            <Button size="sm" variant="outline" onClick={() => handleUpdateTaskStatus(task.task_id, 'in_progress')}>
                              Start
                            </Button>
                          )}
                          {isAgent && task.status === 'in_progress' && (
                            <Button size="sm" className="bg-emerald-600" onClick={() => handleUpdateTaskStatus(task.task_id, 'completed')}>
                              Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Email Requests Tab */}
          <TabsContent value="emails" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowEmailDialog(true)}>
                <Mail className="w-4 h-4 mr-2" />
                Send Email Request
              </Button>
            </div>
            
            {emails.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No Email Requests</h3>
                  <p className="text-slate-500 mb-4">Send formal requests to your managing agent.</p>
                  <Button onClick={() => setShowEmailDialog(true)}>
                    <Mail className="w-4 h-4 mr-2" />
                    Send First Email
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {emails.map(email => (
                  <Card key={email.email_id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold">{email.subject}</h4>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{email.body}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                            <span>From: {email.from_user_name}</span>
                            <span>To: {email.to_agent_name || 'Managing Agent'}</span>
                            <span>{formatDate(email.created_at)}</span>
                          </div>
                        </div>
                        <Badge className={getStatusBadge(email.status)}>{email.status}</Badge>
                      </div>
                      {email.reply && (
                        <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                          <p className="text-sm font-medium text-emerald-800">Reply:</p>
                          <p className="text-sm text-emerald-700">{email.reply}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Trustee-Only Chat Tab */}
          <TabsContent value="trustee-chat" className="mt-4">
            <Card className="h-[60vh]">
              <CardHeader className="py-3 px-4 border-b">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Trustees Only Chat
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 flex flex-col h-[calc(60vh-60px)]">
                <ScrollArea className="flex-1 p-4">
                  <div className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Private chat room for trustees.</p>
                    <p className="text-sm">Discuss matters before sending to managing agent.</p>
                  </div>
                </ScrollArea>
                <div className="p-3 border-t flex gap-2">
                  <Input
                    placeholder="Type a message to fellow trustees..."
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        setActiveChatType('trustee_only');
                        setMessageInput(e.target.value);
                        handleSendMessage();
                        e.target.value = '';
                      }
                    }}
                    className="flex-1"
                  />
                  <Button>
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements" className="mt-4">
            <div className="flex justify-end mb-4">
              <Button onClick={() => setShowAnnouncementDialog(true)}>
                <Megaphone className="w-4 h-4 mr-2" />
                Send Announcement
              </Button>
            </div>
            
            {announcements.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Megaphone className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No Announcements</h3>
                  <p className="text-slate-500 mb-4">Send announcements to all residents (requires managing agent approval).</p>
                  <Button onClick={() => setShowAnnouncementDialog(true)}>
                    <Megaphone className="w-4 h-4 mr-2" />
                    Create Announcement
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {announcements.map(ann => (
                  <Card key={ann.announcement_id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">{ann.category}</Badge>
                            <Badge className={getStatusBadge(ann.status)}>{ann.status.replace('_', ' ')}</Badge>
                          </div>
                          <h4 className="font-semibold">{ann.title}</h4>
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{ann.content}</p>
                          <p className="text-xs text-slate-500 mt-2">By {ann.created_by_name} • {formatDate(ann.created_at)}</p>
                        </div>
                        {isAgent && ann.status === 'pending_approval' && (
                          <div className="flex gap-2">
                            <Button size="sm" className="bg-emerald-600" onClick={() => handleApproveAnnouncement(ann.announcement_id)}>
                              Approve
                            </Button>
                            <Button size="sm" variant="outline">
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                      {ann.rejection_reason && (
                        <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-sm text-red-700">Rejected: {ann.rejection_reason}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-indigo-600" />
                    Building Calendar
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Manage events and view facility bookings
                  </p>
                </div>
                <Button onClick={() => navigate('/dashboard/calendar')} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
                  <Calendar className="w-4 h-4" />
                  Open Full Calendar
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  <div className="p-6 border rounded-lg bg-slate-50">
                    <h3 className="font-medium text-slate-900 mb-4">Quick Actions</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <Button variant="outline" className="justify-start h-auto py-3" onClick={() => navigate('/dashboard/calendar')}>
                        <Plus className="w-4 h-4 mr-2 text-emerald-600" />
                        <div className="text-left">
                          <p className="font-medium">Add Event</p>
                          <p className="text-xs text-slate-500">Create building event</p>
                        </div>
                      </Button>
                      <Button variant="outline" className="justify-start h-auto py-3" onClick={() => navigate('/dashboard/calendar')}>
                        <CalendarDays className="w-4 h-4 mr-2 text-blue-600" />
                        <div className="text-left">
                          <p className="font-medium">View Calendar</p>
                          <p className="text-xs text-slate-500">Events & bookings</p>
                        </div>
                      </Button>
                      <Button variant="outline" className="justify-start h-auto py-3" onClick={() => navigate('/agent/bookings')}>
                        <Building2 className="w-4 h-4 mr-2 text-purple-600" />
                        <div className="text-left">
                          <p className="font-medium">Manage Bookings</p>
                          <p className="text-xs text-slate-500">Facility reservations</p>
                        </div>
                      </Button>
                    </div>
                  </div>
                  <div className="text-center py-6 text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm">Click "Open Full Calendar" to view and manage all events</p>
                    <p className="text-xs mt-1">Bookings made by residents automatically appear in the calendar</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-base">Building Documents</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    Upload and manage documents for residents (pet policy, welcome pack, building rules, etc.)
                  </p>
                </div>
                <Button onClick={() => navigate('/admin/documents')} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Manage Documents
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  <div className="p-6 border-2 border-dashed rounded-lg text-center">
                    <File className="w-12 h-12 mx-auto mb-3 text-slate-400" />
                    <h3 className="font-medium text-slate-900 mb-2">Document Categories</h3>
                    <div className="flex flex-wrap gap-2 justify-center mb-4">
                      <Badge variant="outline">Pet Policy</Badge>
                      <Badge variant="outline">Building Rules</Badge>
                      <Badge variant="outline">Welcome Pack</Badge>
                      <Badge variant="outline">AGM Minutes</Badge>
                      <Badge variant="outline">Financial Reports</Badge>
                      <Badge variant="outline">Insurance</Badge>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">
                      Click "Manage Documents" to upload new documents or manage existing ones.
                      Uploaded documents will be visible to all residents in their Documents section.
                    </p>
                    <Button variant="outline" onClick={() => navigate('/admin/documents')}>
                      <FileText className="w-4 h-4 mr-2" />
                      Go to Document Vault
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="mt-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(tmpl => (
                <Card key={tmpl.template_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleUseTemplate(tmpl)}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <Badge variant="outline">{tmpl.template_type}</Badge>
                      <Badge className="bg-slate-100 text-slate-600">{tmpl.category}</Badge>
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">{tmpl.name}</h4>
                    <p className="text-sm text-slate-600 line-clamp-3">{tmpl.body}</p>
                    <Button variant="outline" className="w-full mt-3" size="sm">
                      Use Template
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* History/Logs Tab */}
          <TabsContent value="history" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Communication History</CardTitle>
              </CardHeader>
              <CardContent>
                {logs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No communication history yet.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {logs.map(log => (
                      <div key={log.log_id} className="flex items-start gap-3 p-3 border rounded-lg">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          log.log_type === 'message' ? 'bg-indigo-100' :
                          log.log_type === 'task' ? 'bg-amber-100' :
                          log.log_type === 'email' ? 'bg-blue-100' :
                          log.log_type === 'announcement' ? 'bg-purple-100' : 'bg-slate-100'
                        }`}>
                          {log.log_type === 'message' && <MessageSquare className="w-4 h-4 text-indigo-600" />}
                          {log.log_type === 'task' && <ListTodo className="w-4 h-4 text-amber-600" />}
                          {log.log_type === 'email' && <Mail className="w-4 h-4 text-blue-600" />}
                          {log.log_type === 'announcement' && <Megaphone className="w-4 h-4 text-purple-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{log.summary}</p>
                          <p className="text-xs text-slate-500">
                            By {log.actor_name} • {formatDate(log.created_at)}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">{log.action}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transfers Tab - Trustee Only */}
          {!isAgent && (
            <TabsContent value="transfers" className="mt-4">
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ArrowRightLeft className="w-5 h-5 text-amber-600" />
                      Building Transfer Requests
                    </CardTitle>
                    <Button onClick={() => setShowTransferDialog(true)} data-testid="new-transfer-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      New Transfer Request
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {myTransferRequests.length === 0 ? (
                    <div className="text-center py-12">
                      <ArrowRightLeft className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p className="text-slate-500">No transfer requests</p>
                      <p className="text-xs text-slate-400 mt-1">
                        Transfer your building to a managing agent to get professional management
                      </p>
                      <Button 
                        className="mt-4"
                        onClick={() => setShowTransferDialog(true)}
                      >
                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                        Transfer to Managing Agent
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {myTransferRequests.map(transfer => (
                        <div key={transfer.transfer_id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                transfer.status === 'pending' ? 'bg-amber-100' :
                                transfer.status === 'accepted' ? 'bg-emerald-100' :
                                transfer.status === 'rejected' ? 'bg-red-100' : 'bg-slate-100'
                              }`}>
                                <Building2 className={`w-5 h-5 ${
                                  transfer.status === 'pending' ? 'text-amber-600' :
                                  transfer.status === 'accepted' ? 'text-emerald-600' :
                                  transfer.status === 'rejected' ? 'text-red-600' : 'text-slate-600'
                                }`} />
                              </div>
                              <div>
                                <p className="font-medium text-slate-900">{transfer.building_name}</p>
                                <p className="text-sm text-slate-500">
                                  To: <span className="font-medium">{transfer.target_company_name}</span>
                                </p>
                                <p className="text-xs text-slate-400 mt-1">
                                  Requested {new Date(transfer.created_at).toLocaleDateString()}
                                </p>
                                {transfer.notes && (
                                  <p className="text-xs text-slate-500 mt-1 italic">"{transfer.notes}"</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={
                                transfer.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                transfer.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                                transfer.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-700'
                              }>
                                {transfer.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                                {transfer.status === 'accepted' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                {transfer.status === 'rejected' && <X className="w-3 h-3 mr-1" />}
                                {transfer.status.charAt(0).toUpperCase() + transfer.status.slice(1)}
                              </Badge>
                              {transfer.status === 'pending' && (
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleCancelTransfer(transfer.transfer_id)}
                                >
                                  Cancel
                                </Button>
                              )}
                            </div>
                          </div>
                          {transfer.status === 'accepted' && (
                            <div className="mt-3 p-2 bg-emerald-50 rounded border border-emerald-200 text-sm text-emerald-700">
                              <CheckCircle2 className="w-4 h-4 inline mr-1" />
                              Transfer completed on {new Date(transfer.resolved_at).toLocaleDateString()}.
                              Your building is now managed by {transfer.target_company_name}.
                            </div>
                          )}
                          {transfer.status === 'rejected' && (
                            <div className="mt-3 p-2 bg-red-50 rounded border border-red-200 text-sm text-red-700">
                              <AlertCircle className="w-4 h-4 inline mr-1" />
                              Transfer request was declined.
                              {transfer.resolution_notes && <span className="block mt-1">Reason: {transfer.resolution_notes}</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </main>

      {/* Create Task Dialog */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Task Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Task Type</Label>
              <Select value={taskForm.task_type} onValueChange={(v) => setTaskForm({...taskForm, task_type: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm({...taskForm, title: e.target.value})}
                placeholder="Brief title for the task"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm({...taskForm, description: e.target.value})}
                placeholder="Detailed description of what needs to be done..."
                rows={4}
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({...taskForm, priority: v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm({...taskForm, due_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTask} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Email to Managing Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject *</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                placeholder="Email subject"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm({...emailForm, body: e.target.value})}
                placeholder="Your message to the managing agent..."
                rows={6}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Send Email</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Announcement Dialog */}
      <Dialog open={showAnnouncementDialog} onOpenChange={setShowAnnouncementDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Announcement to Residents</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                This announcement requires managing agent approval before being sent to residents.
              </p>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={announcementForm.category} onValueChange={(v) => setAnnouncementForm({...announcementForm, category: v})}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ANNOUNCEMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                placeholder="Announcement title"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={announcementForm.content}
                onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                placeholder="Write your announcement to residents..."
                rows={6}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnnouncementDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAnnouncement} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Megaphone className="w-4 h-4 mr-2" />Submit for Approval</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer to Managing Agent Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-amber-600" />
              Transfer Building to Managing Agent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                This will transfer management of your building to a managing agent company. You will retain your trustee access but the agent will become responsible for subscription payments.
              </p>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium">What happens after transfer:</p>
              <ul className="text-xs text-slate-600 mt-2 space-y-1">
                <li>• All data (tickets, documents, residents) is preserved</li>
                <li>• You retain trustee access</li>
                <li>• Agent becomes responsible for payments</li>
                <li>• No disruption to residents</li>
              </ul>
            </div>

            <div>
              <Label>Search Managing Agent Company</Label>
              <div className="relative mt-1">
                <Input
                  placeholder="Enter company name or email..."
                  onChange={(e) => {
                    setTransferForm({ ...transferForm, target_company_email: e.target.value });
                    handleSearchCompanies(e.target.value);
                  }}
                  value={transferForm.target_company_email}
                />
                {searchingCompany && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                )}
              </div>
              
              {/* Search Results */}
              {companySearchResults.length > 0 && (
                <div className="mt-2 border rounded-lg divide-y max-h-40 overflow-y-auto">
                  {companySearchResults.map(company => (
                    <button
                      key={company.company_id}
                      className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
                      onClick={() => handleSelectCompany(company)}
                    >
                      <Briefcase className="w-4 h-4 text-indigo-600" />
                      <div>
                        <p className="text-sm font-medium">{company.name}</p>
                        {company.email && <p className="text-xs text-slate-500">{company.email}</p>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Selected Company */}
              {selectedCompany && (
                <div className="mt-2 p-3 bg-indigo-50 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="text-sm font-medium text-indigo-900">{selectedCompany.name}</p>
                      {selectedCompany.email && <p className="text-xs text-indigo-600">{selectedCompany.email}</p>}
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => {
                    setSelectedCompany(null);
                    setTransferForm({ ...transferForm, target_company_email: '', target_company_name: '' });
                  }}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={transferForm.notes}
                onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                placeholder="Any notes for the managing agent..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowTransferDialog(false);
              setTransferForm({ target_company_email: '', target_company_name: '', notes: '' });
              setSelectedCompany(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitTransfer} 
              disabled={submitting || (!transferForm.target_company_email && !transferForm.target_company_name)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Send Transfer Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Managing Agent Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-indigo-600" />
              Invite a Managing Agent
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
              <p className="text-sm text-indigo-800">
                <UserPlus className="w-4 h-4 inline mr-1" />
                Invite a professional managing agent company to help manage your building. They will receive an email invitation.
              </p>
            </div>

            <div>
              <Label>Company Name *</Label>
              <Input
                placeholder="e.g., PropManage SA"
                value={inviteForm.company_name}
                onChange={(e) => setInviteForm({...inviteForm, company_name: e.target.value})}
                className="mt-1"
                data-testid="invite-company-name"
              />
            </div>

            <div>
              <Label>Agent Email Address *</Label>
              <Input
                type="email"
                placeholder="agent@company.com"
                value={inviteForm.agent_email}
                onChange={(e) => setInviteForm({...inviteForm, agent_email: e.target.value})}
                className="mt-1"
                data-testid="invite-agent-email"
              />
              <p className="text-xs text-slate-500 mt-1">The agent will receive an invitation email at this address</p>
            </div>

            <div>
              <Label>Personal Message (Optional)</Label>
              <Textarea
                placeholder="Add a personal note to your invitation..."
                value={inviteForm.message}
                onChange={(e) => setInviteForm({...inviteForm, message: e.target.value})}
                className="mt-1"
                rows={3}
                data-testid="invite-message"
              />
            </div>

            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700">What happens next:</p>
              <ul className="text-xs text-slate-600 mt-2 space-y-1">
                <li>• The agent receives an email invitation</li>
                <li>• They can accept or decline the invitation</li>
                <li>• Upon acceptance, they are assigned to manage your building</li>
                <li>• You remain as the trustee with full access</li>
              </ul>
            </div>

            {/* Show sent invitations */}
            {myInvitations && myInvitations.length > 0 && (
              <div className="border-t pt-4">
                <Label className="mb-2 block">Your Sent Invitations</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {myInvitations.map(inv => (
                    <div key={inv.invitation_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                      <div>
                        <p className="font-medium">{inv.company_name}</p>
                        <p className="text-xs text-slate-500">{inv.agent_email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          inv.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          inv.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {inv.status}
                        </span>
                        {inv.status === 'pending' && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-7 w-7 text-red-500"
                            onClick={() => handleCancelInvitation(inv.invitation_id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowInviteDialog(false);
              setInviteForm({ company_name: '', agent_email: '', message: '' });
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendInvitation} 
              disabled={sendingInvite || !inviteForm.agent_email || !inviteForm.company_name}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="send-invitation-btn"
            >
              {sendingInvite ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
